import AsyncStorage from '@react-native-async-storage/async-storage';
import { getAttibutes, isVn } from '@global/helpers';
import { MsB2b } from '@api/ms-b2b';
import { getPriceRules, getPriceRulesProduct } from '@api/ms-catalog';
import { resolvePromoStatus } from '../todo-visibility-update/TodoVisibilityUpdate.utils';

const REQUEST_TIMEOUT_MS = 15000;
const VISIBILITY_PRODUCT_META_CACHE_PREFIX = 'B2B-VISIBILITY-PRODUCT-META';
const PRODUCT_META_CACHE_TTL_MS = 10 * 60 * 1000;
const DEFAULT_PRICE_CACHE_TTL_MS = 60 * 60 * 1000;

let defaultPriceRuleSessionCache = {
  key: '',
  ruleId: '',
  updatedAt: 0,
};
const defaultPriceByCombinationSessionCache = {};

export const requestWithTimeout = async (
  requestPromise,
  timeoutMs = REQUEST_TIMEOUT_MS,
) => {
  let timeoutId = null;
  const safeRequest = requestPromise
    .then((response) => response)
    .catch(() => null);
  const timeoutPromise = new Promise((resolve) => {
    timeoutId = setTimeout(() => resolve(null), timeoutMs);
  });
  const result = await Promise.race([safeRequest, timeoutPromise]);
  if (timeoutId) {
    clearTimeout(timeoutId);
  }
  return result;
};

export const convertAttribute = (combination) => {
  let variant = '';
  if (combination?.attributes && Object.keys(combination.attributes).length) {
    variant = getAttibutes(combination.attributes);
  }
  return variant;
};

export const getImage = (data) => {
  const getCoverImageUrl = (images = []) => {
    if (!Array.isArray(images) || !images.length) {
      return '';
    }
    const coverImage = images.find((image) => image?.is_cover && image?.url);
    if (coverImage?.url) {
      return coverImage.url;
    }
    const firstImage = images.find((image) => image?.url);
    return firstImage?.url || '';
  };

  return (
    getCoverImageUrl(data?.images) ||
    data?.image ||
    getCoverImageUrl(data?.combination?.images) ||
    data?.combination?.image ||
    getCoverImageUrl(data?.product?.images) ||
    data?.product?.image ||
    ''
  );
};

export const getCombinationData = (data, type = 'reference') => {
  let result = '-';
  const combination = data?.combination
    ? data.combination
    : data?.combinations?.length
    ? data.combinations[0]
    : {};
  if (type == 'reference') {
    if (combination?.reference) {
      result = combination.reference;
    }
  } else {
    if (combination) {
      result = convertAttribute(combination);
    }
    if (combination?.variant) {
      result = combination.variant;
    }
  }
  return result;
};

export const getCombinationId = (product = {}) => {
  return (
    product?.combination?.id ||
    product?.combination?._id ||
    product?.combination_id ||
    product?.product_combination_id ||
    ''
  );
};

export const getVisibilityProductMetaCacheKey = ({
  activityId = '',
  customerAddressId = '',
  productId = '',
  combinationId = '',
}) => {
  if (!productId || !combinationId) {
    return '';
  }
  return `${VISIBILITY_PRODUCT_META_CACHE_PREFIX}-${activityId || 'global'}-${
    customerAddressId || 'no-address'
  }-${productId}-${combinationId}`;
};

export const readProductMetaCache = async (key) => {
  if (!key) {
    return null;
  }
  try {
    const raw = await AsyncStorage.getItem(key);
    return raw ? JSON.parse(raw) : null;
  } catch (error) {
    return null;
  }
};

export const writeProductMetaCache = async (key, payload = {}) => {
  if (!key) {
    return;
  }
  try {
    await AsyncStorage.setItem(
      key,
      JSON.stringify({
        ...(payload || {}),
        updatedAt: new Date().toISOString(),
      }),
    );
  } catch (error) {}
};

export const analyzeCache = (cachedMeta) => {
  const cachedUpdatedAtMs = cachedMeta?.updatedAt
    ? new Date(cachedMeta.updatedAt).getTime()
    : 0;
  const isMetaCacheFresh = Boolean(
    cachedUpdatedAtMs &&
      Date.now() - cachedUpdatedAtMs < PRODUCT_META_CACHE_TTL_MS,
  );

  const cachedDefaultPrice = Number(cachedMeta?.defaultPrice || 0);
  const cachedValueSellOutObj = {
    default: cachedDefaultPrice || 0,
    final_price: 0,
  };

  return { isMetaCacheFresh, cachedDefaultPrice, cachedValueSellOutObj };
};

export const constructBaseProduct = (item, isOther, cachedValueSellOutObj) => {
  const combinations = item?.combination
    ? [item.combination]
    : item?.combinations || [];
  const baseProduct = {
    id: item?.product_id || item?.id || '',
    name: item?.name || '',
    image: getImage(item),
    brand:
      item?.brand && Object.keys(item.brand).length
        ? item.brand
        : { id: '', name: '' },
    quantity: item?.quantity || 0,
    combination: {},
    is_selected: item?.is_selected || false,
    valueSellOut: cachedValueSellOutObj,
    promo_status: resolvePromoStatus(item),
    promo_list: item?.promo_list ?? [],
    is_wobbler: item?.is_wobbler ?? null,
    notes: item?.notes ?? '',
  };

  if (!item?.combination && combinations.length) {
    const combination = combinations[0];
    baseProduct.combination = {
      id: combination?._id,
      reference: combination?.reference || '',
      variant: convertAttribute(combination),
      is_other: isOther,
    };
  } else if (item?.combination) {
    baseProduct.combination = item?.combination?.variant
      ? item.combination
      : {
          id: item?.combination?._id || item?.combination?.id,
          reference: item?.combination?.reference || '',
          variant: convertAttribute(item.combination),
          is_other: isOther,
        };
  }
  return baseProduct;
};

export const getDefaultSellOutPrice = async (productCombinationId) => {
  if (!productCombinationId) {
    return 0;
  }
  const isVnFilter = isVn() ? 'b2b_vn' : 'b2b';
  const now = Date.now();
  const combinationCacheKey = `${isVnFilter}-${productCombinationId}`;
  const combinationCached =
    defaultPriceByCombinationSessionCache[combinationCacheKey];
  if (
    combinationCached?.updatedAt &&
    now - combinationCached.updatedAt < DEFAULT_PRICE_CACHE_TTL_MS
  ) {
    return Number(combinationCached.price || 0);
  }
  const filter = {
    is_active: true,
    is_default: true,
    is_active_for: { $in: [isVnFilter] },
  };
  let priceRulesId = '';
  if (
    defaultPriceRuleSessionCache?.ruleId &&
    defaultPriceRuleSessionCache?.key === isVnFilter &&
    now - defaultPriceRuleSessionCache.updatedAt < DEFAULT_PRICE_CACHE_TTL_MS
  ) {
    priceRulesId = defaultPriceRuleSessionCache.ruleId;
  } else {
    const priceRules = await requestWithTimeout(
      getPriceRules({ params: { filter } }),
    );
    const priceRulesData = priceRules?.data?.data;
    priceRulesId = priceRulesData?.length ? priceRulesData[0]._id : '';
    defaultPriceRuleSessionCache = {
      key: isVnFilter,
      ruleId: priceRulesId || '',
      updatedAt: Date.now(),
    };
  }
  if (!priceRulesId) {
    return 0;
  }

  const priceRulesProduct = await requestWithTimeout(
    getPriceRulesProduct(
      {
        params: {
          filter: { product_combination_id: productCombinationId },
        },
      },
      priceRulesId,
    ),
  );
  const finalPrice = priceRulesProduct?.data?.data?.[0]?.final_price || 0;
  const parsedPrice = Number(finalPrice) || 0;
  defaultPriceByCombinationSessionCache[combinationCacheKey] = {
    price: parsedPrice,
    updatedAt: Date.now(),
  };
  return parsedPrice;
};

export const fetchRemoteData = async (
  itemCombinationId,
  itemProductId,
  customerAddressId,
) => {
  const requests = [];
  requests.push(getDefaultSellOutPrice(itemCombinationId));

  if (customerAddressId && itemProductId && itemCombinationId) {
    requests.push(
      requestWithTimeout(
        MsB2b.getVisibilityProductStock(
          customerAddressId,
          itemProductId,
          itemCombinationId,
        ),
      ),
    );
  }

  const [defaultPriceResult, stockResultResponse] = await Promise.all(requests);
  return {
    defaultPrice: Number(defaultPriceResult || 0),
    stockResult: stockResultResponse || null,
  };
};

export const calculateSellOut = (defaultPrice, sellOutQty) => {
  const qty = Number(sellOutQty || 0);
  return {
    default: defaultPrice,
    final_price: qty > 0 ? defaultPrice * qty : 0,
  };
};

export const prepareAlreadyInitiatedData = (item) => {
  const prevStock = Number(item?.stock ?? item?.quantity ?? 0);
  const sellIn = Number(item?.sell_in ?? 0);
  const sellOut = Number(item?.sell_out ?? 0);
  const valueReturn = Number(item?.return ?? 0);
  const endingStockCalc = prevStock + sellIn - sellOut - valueReturn;

  return {
    ...item,
    stock: prevStock,
    is_initiate: true,
    is_selected: true,
    endingStock: endingStockCalc,
    valueSellOut: calculateSellOut(
      Number(item?.valueSellOut?.default || 0),
      item?.sell_out,
    ),
  };
};

export const prepareSelectedDataSync = (
  item,
  haveSelectedData,
  nextValueSellOut,
  stockResult,
) => {
  const savedData = { ...item, ...haveSelectedData };
  const prevStock =
    Number(savedData?.stock ?? savedData?.quantity ?? 0) ||
    Number(savedData?.previous_stock ?? 0);
  const sellIn = Number(savedData?.sell_in ?? 0);
  const sellOut = Number(savedData?.sell_out ?? 0);
  const valueReturn = Number(savedData?.return ?? 0);
  const endingStockCalc = prevStock + sellIn - sellOut - valueReturn;

  const finalValueSellOut =
    nextValueSellOut?.default > 0
      ? calculateSellOut(
          nextValueSellOut.default,
          haveSelectedData?.sell_out ?? item?.sell_out,
        )
      : item?.valueSellOut || { default: 0, final_price: 0 };

  return {
    ...item,
    ...haveSelectedData,
    ...(stockResult?.data?.data || {}),
    stock: prevStock,
    endingStock: endingStockCalc,
    is_selected: true,
    is_initiate: true,
    is_beginning: haveSelectedData?.is_beginning ?? item?.is_beginning ?? false,
    valueSellOut: finalValueSellOut,
  };
};
